import {
    FormType,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { build, stack } from '../../../../../common/classes/request-builder';
import { environment } from 'src/environments/environment';
import { UploadApiService } from 'src/app/service/api/upload-api.service';
import { ClientesApiService } from 'src/app/service/api/clientes-api.service';
import { RopoApiService } from '../../../../../service/api/ropo-api.service';
import { VentasApiService } from 'src/app/service/api/ventas-api.service';
import { firstValueFrom } from 'rxjs';

import { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import { ClienteModel } from 'src/app/models/cliente.model';
import { MantenimientoFormDefinition } from './form-control/form-definition';

const aplicadorName = environment.features.aplicadorName;
const HTTP_STATUS_OK = 200;

type RopoResponse = {
    ropo?: {
        código?: string;
        caducidad?: string;
        nivel?: string;
    };
};

@Component({
    selector: 'app-mantenimiento-form',
    standalone: false,
    templateUrl: './mantenimiento-form.component.html',
    styleUrls: ['./mantenimiento-form.component.scss']
})
export class MantenimientoFormComponent extends BaseForm<MantenimientoModel> implements OnInit {
    public appName = environment.appName;

    public showTrabajadoresFirma = environment.features.showTrabajadoresFirma;

    public showRopoRomaRegepa = environment.features.showRopoRomaRegepa;

    public override model: MantenimientoModel = {} as MantenimientoModel;

    public form!: MantenimientoFormDefinition;


    private hasCheckedExistingSignature = false;

    public constructor(
        public override dashboard: DashboardService,
        public override router: Router,
        public override route: ActivatedRoute,
        public trabajadoresApi: TrabajadoresApiService,
        public ventasApi: VentasApiService,
        public clientesApi: ClientesApiService,
        public ropoService: RopoApiService,
        public uploadApi: UploadApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            trabajadoresApi.trabajadoresPUT,
            trabajadoresApi.trabajadoresPOST,
            'mantenimiento',
            'Actualizar ' + aplicadorName,
            'Crear ' + aplicadorName,
            'Duplicar ' + aplicadorName,
        );
    }// constructor();

    public async ngOnInit(): Promise<void> {
        this.initialiseReactiveDefaults();
        this.form = new MantenimientoFormDefinition(this.getType(), this.model);
        await this.initFormData();
        this.softInit(this.getType());
    }// ngOnInit();

    public stableValueChanges(event: ReactiveFormStableValueChange<MantenimientoModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        if (this.isRepeatableGroupRuntimeChange(changedFieldName)) {
            this.form.updateModelSnapshot(event.value, event.changedField);
            this.model = this.form.model as MantenimientoModel;
            return;
        }// if();

        this.form.updateModel(event.value, event.changedField);
        this.model = this.form.model as MantenimientoModel;

        if (event.changedField === '__previous_data_patch') {
            this.applyLoadedReactiveState();
            return;
        }// if();

        if (event.changedField === '__dropdown_refresh') {
            this.model = this.form.model as MantenimientoModel;
            return;
        }// if();

        this.formChanges(changedFieldName);
    }// stableValueChanges();

    public reactiveFormChanges(
        formModel: ReactiveFormValue<MantenimientoModel>,
        lastChangedFieldName: ReactiveFormChangedField<MantenimientoModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: lastChangedFieldName,
            changedFields: [String(lastChangedFieldName ?? '')],
            origin: 'user',
        });
    }// reactiveFormChanges();

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<MantenimientoModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }// if();

        this.form.updateModel(event.value, event.changedField);
        this.model = this.form.model as MantenimientoModel;
        this.applyLoadedReactiveState();
    }// onReactiveFormLifecycleEvent();

    public formChanges(tag: string): void {
        if (tag === 'nif') {
            this.applyRopoDataFromNif();
        }// if();

        if (tag === 'id_comercio' && this.model.id_comercio === undefined) {
            this.model.id_comercio = null;
            this.form.updateModel(this.model as ReactiveFormValue<MantenimientoModel>, tag as ReactiveFormChangedField<MantenimientoModel>);
        }// if();

        if (this.shouldRefreshReactiveDropdownMappings(tag)) {
            this.refreshReactiveDropdownMappings();
        }// if();
    }// formChanges();

    public onAfterSend(response: MantenimientoModel[] | MantenimientoModel): void {
        const signature = String(this.model.signature_img ?? '').trim();

        if (!this.isBase64Signature(signature)) {
            return;
        }// if();

        const workerId = this.getSignatureWorkerId(response);

        if (!workerId) {
            console.warn('No se ha podido guardar la firma porque el trabajador no tiene identificador.');
            return;
        }// if();

        void this.uploadApi.firmasPOST.toPromise({
            id: workerId,
            dir: 'firmas_trabajadores',
            firma: signature,
        });
    }// onAfterSend();

    public verAdjunto(): void {
        const myWindow = window.open('about:blank', '_blank', 'width=600, height=600');

        myWindow?.location.assign(environment.serverUrl + 'fotos_trabajadores/' + this.model.adjunto);
        myWindow?.focus();
    }// verAdjunto();

    private initFormData(): Promise<boolean> {
        const builds = [
            build(this.trabajadoresApi.maquinariaGET, this.form.lists.maquinaria, 'nombre', true),
        ];

        if (this.form.showTrabajadoresCargos) {
            builds.push(build(this.trabajadoresApi.cargosGET, this.form.lists.cargos, 'nombre'));
        }// if();

        if (this.form.showTrabajadoresClienteAsignado) {
            builds.push(build(
                this.clientesApi.clientesGET,
                this.form.lists.clientes,
                (it: ClienteModel) => it.nombre + ' ' + (it.apellidos || ''),
            ));
        }// if();

        if (this.form.showTrabajadoresCuadrillas) {
            builds.push(build(
                this.trabajadoresApi.cuadrillas.GET,
                this.form.lists.cuadrillas,
                (it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || ''),
            ));
        }// if();

        if (this.form.activateREA || this.appName === 'sirga') {
            builds.push(build(this.ventasApi.compradoresGET, this.form.lists.comercios, 'razon_social', true));
        }// if();

        return stack(builds).then((result: boolean) => {
            this.refreshReactiveDropdownMappings();

            return result;
        });
    }// initFormData();


    private refreshReactiveDropdownMappings(): void {
        this.finalizeExternalDropdowns();
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }// refreshReactiveDropdownMappings();

    private shouldRefreshReactiveDropdownMappings(tag: string): boolean {
        return [
            'id_comercio',
            'id_cliente',
            'id_maquina',
            'id_cargo',
            'cuadrilla',
            '__previous_data_patch',
        ].includes(tag);
    }// shouldRefreshReactiveDropdownMappings();

    private isRepeatableGroupRuntimeChange(tag: string): boolean {
        return [
            'carnetsGroup',
            'tipo_carnet',
            'ropo',
            'fecha_exp',
            'fecha_cad',
        ].includes(tag);
    }// isRepeatableGroupRuntimeChange();

    private initialiseReactiveDefaults(): void {
        if (this.getType() === FormType.CREATION) {
            this.model.activo = '1';
        }// if();
    }// initialiseReactiveDefaults();

    private applyLoadedReactiveState(): void {
        this.form.updateModel(this.model as ReactiveFormValue<MantenimientoModel>, '__previous_data_patch');
        this.model = this.form.model as MantenimientoModel;
        this.refreshReactiveDropdownMappings();
        void this.patchExistingSignatureIfAvailable();
    }// applyLoadedReactiveState();

    private async patchExistingSignatureIfAvailable(): Promise<void> {
        const workerId = String(this.getRegisterId() || '').trim();

        if (!this.showTrabajadoresFirma || this.hasCheckedExistingSignature || this.getType() !== FormType.EDITION || !workerId) {
            return;
        }// if();

        this.hasCheckedExistingSignature = true;
        const signatureUrl = this.getSignatureUrl(workerId);

        try {
            const response = await fetch(signatureUrl);

            if (response.status !== HTTP_STATUS_OK) {
                return;
            }// if();

            this.model.signature_img = signatureUrl;
            this.form.updateModel(this.model as ReactiveFormValue<MantenimientoModel>, 'signature_img');
            this.model = this.form.model as MantenimientoModel;
            this.formBuilderRef?.updateForm({ signature_img: signatureUrl }, false);
        } catch (error) {
            console.warn('No se ha podido comprobar la firma del trabajador.', error);
        }// try/catch;
    }// patchExistingSignatureIfAvailable();

    private getSignatureWorkerId(response: MantenimientoModel[] | MantenimientoModel): string {
        const currentId = String(this.model.id ?? this.getRegisterId() ?? '').trim();

        if (currentId) {
            return currentId;
        }// if();

        if (Array.isArray(response)) {
            return String(response[0]?.id ?? '').trim();
        }// if();

        return String(response?.id ?? '').trim();
    }// getSignatureWorkerId();

    private getSignatureUrl(workerId: string): string {
        return this.pathToFirmas + '_trabajadores/' + workerId + '.png';
    }// getSignatureUrl();

    private isBase64Signature(signature: string): boolean {
        return signature.startsWith('data:image/');
    }// isBase64Signature();

    private applyRopoDataFromNif(): void {
        const nif = String(this.model.nif ?? '');

        if (!this.showRopoRomaRegepa || nif.length < 9) {
            return;
        }// if();

        firstValueFrom(this.ropoService.getROPO(nif))
            .then((resp: RopoResponse) => {
                if (!resp?.ropo) {
                    return;
                }// if();

                this.model.ropo = resp.ropo.código ?? '';
                this.model.fecha_cad = resp.ropo.caducidad ?? '';
                this.model.tipo_carnet = String(resp.ropo.nivel ?? '').includes('cualificado') ? 'CUALIFICADO' : 'BÁSICO';
                this.form.updateModel(this.model as ReactiveFormValue<MantenimientoModel>, 'nif');
                this.model = this.form.model as MantenimientoModel;
                this.formBuilderRef?.updateForm(this.form.model as Record<string, unknown>, false);
                return;
            })
            .catch((error: string) => console.log('error', error));
    }// applyRopoDataFromNif();
}
